export const REGIONS = [
  {
    id: 'europe',
    label: 'Europe',
    description: 'European continental region',
  },
  {
    id: 'north-america',
    label: 'North America',
    description: 'North American continental region',
  },
] as const;

export const VARIABLES = [
  {
    id: '2t',
    label: 'Air Temperature 2m [°C]',
    juaId: 'air_temperature_at_height_level_2m',
    externalId: 'temperature_2m',
    originalUnit: 'Kelvin',
    displayUnit: '°C',
    description: 'Temperature measured at 2 meters above ground',
    showTimeSeries: true,
    rescale: '260,315', // -43.15°C to 41.85°C in Kelvin
    supportedWeightings: ['population'],
  },
  {
    id: '2dt',
    label: 'Dew Point Temperature 2m [°C]',
    juaId: 'dew_point_temperature_at_height_level_2m',
    externalId: 'dew_point_2m',
    originalUnit: 'Kelvin',
    displayUnit: '°C',
    description: 'Dew Point Temperature measured at 2 meters above ground',
    showTimeSeries: true,
    rescale: '260,315', // -43.15°C to 41.85°C in Kelvin
    supportedWeightings: ['population'],
  },
  {
    id: 'precipitation',
    label: 'Precipitation [mm]',
    juaId: 'precipitation_amount_sum_1h',
    externalId: 'precipitation',
    originalUnit: 'mm',
    displayUnit: 'mm',
    description: 'Total precipitation amount',
    showTimeSeries: true,
    rescale: '0,50',
    supportedWeightings: ['population'],
  },
  {
    id: '10si',
    label: 'Wind Speed 10m [m s⁻¹]',
    juaId: 'wind_speed_at_height_level_10m',
    externalId: 'wind_speed_10m',
    originalUnit: 'm s⁻¹',
    displayUnit: 'm s⁻¹',
    description: 'Wind Speed measured at 10 meters above ground',
    showTimeSeries: true,
    rescale: '0,25',
    supportedWeightings: ['population', 'wind_capacity'],
  },
  {
    id: '10wdir',
    label: 'Wind Direction 10m [° true]',
    juaId: 'wind_direction_at_height_level_10m',
    externalId: 'wind_direction_10m',
    originalUnit: 'Degrees true',
    displayUnit: '°',
    description: 'Wind Direction measured at 10 meters above ground',
    showTimeSeries: true,
    rescale: '0,360',
    supportedWeightings: ['population'],
  },
  {
    id: '100si',
    label: 'Wind Speed 100m [m s⁻¹]',
    juaId: 'wind_speed_at_height_level_100m',
    externalId: 'wind_speed_100m',
    originalUnit: 'm s⁻¹',
    displayUnit: 'm s⁻¹',
    description: 'Wind Speed measured at 100 meters above ground',
    showTimeSeries: true,
    rescale: '0,30',
    supportedWeightings: ['population', 'wind_capacity'],
  },
  {
    id: '100wdir',
    label: 'Wind Direction 100m [° true]',
    juaId: 'wind_direction_at_height_level_100m',
    externalId: 'wind_direction_100m',
    originalUnit: 'Degrees true',
    displayUnit: '°',
    description: 'Wind Direction measured at 100 meters above ground',
    showTimeSeries: true,
    rescale: '0,360',
    supportedWeightings: ['population'],
  },
  {
    id: 'high_cloud_cover',
    label: 'High Cloud Cover [%]',
    juaId: 'cloud_area_fraction_at_entire_atmosphere_high_type',
    externalId: 'cloud_cover_high',
    originalUnit: '%',
    displayUnit: '%',
    description: 'High altitude cloud coverage',
    showTimeSeries: true,
    rescale: '0,100',
    supportedWeightings: ['population'],
  },
  {
    id: 'medium_cloud_cover',
    label: 'Medium Cloud Cover [%]',
    juaId: 'cloud_area_fraction_at_entire_atmosphere_medium_type',
    externalId: 'cloud_cover_mid',
    originalUnit: '%',
    displayUnit: '%',
    description: 'Medium altitude cloud coverage',
    showTimeSeries: true,
    rescale: '0,100',
    supportedWeightings: ['population'],
  },
  {
    id: 'low_cloud_cover',
    label: 'Low Cloud Cover [%]',
    juaId: 'cloud_area_fraction_at_entire_atmosphere_low_type',
    externalId: 'cloud_cover_low',
    originalUnit: '%',
    displayUnit: '%',
    description: 'Low altitude cloud coverage',
    showTimeSeries: true,
    rescale: '0,100',
    supportedWeightings: ['population'],
  },
  {
    id: 'msl',
    label: 'Mean Sea-level Pressure [hPa]',
    juaId: 'air_pressure_at_mean_sea_level',
    externalId: 'pressure_msl',
    originalUnit: 'Pa',
    displayUnit: 'hPa',
    description: 'Atmospheric pressure adjusted to sea level',
    showTimeSeries: true,
    rescale: '960,1040',
    supportedWeightings: ['population'],
  },
  {
    id: 'z_500',
    label: 'Geopotential [m² s⁻²]',
    juaId: 'geopotential_at_pressure_level_50000Pa',
    externalId: 'geopotential_height_500hPa',
    originalUnit: 'm² s⁻²',
    displayUnit: 'm² s⁻²',
    description: 'Geopotential at 500 hPa pressure level',
    showTimeSeries: true,
    rescale: '48000,58000',
    supportedWeightings: ['population'],
  },
  {
    id: 'relative_humidity',
    label: 'Relative Humidity 2m [%]',
    juaId: 'relative_humidity_at_height_level_2m',
    externalId: 'relative_humidity_2m',
    originalUnit: '%',
    displayUnit: '%',
    description: 'Relative Humidity measured at 2 meters above ground',
    showTimeSeries: true,
    rescale: '0,100',
    supportedWeightings: ['population'],
  },
  {
    id: 'fdir',
    label: 'Surface Direct Solar Radiation [J m⁻²]',
    juaId: 'surface_direct_downwelling_shortwave_flux_sum_1h',
    externalId: 'direct_radiation',
    originalUnit: 'J m⁻²',
    displayUnit: 'J m⁻²',
    description: "Direct solar radiation reaching the Earth's surface",
    showTimeSeries: true,
    rescale: '0,4231308',
    supportedWeightings: ['population', 'solar_capacity'],
  },
  {
    id: 'ssrd',
    label: 'Surface Solar Radiation Downwards [J m⁻²]',
    juaId: 'surface_downwelling_shortwave_flux_sum_1h',
    externalId: 'shortwave_radiation',
    originalUnit: 'J m⁻²',
    displayUnit: 'J m⁻²',
    description: "Solar radiation reaching the Earth's surface",
    showTimeSeries: true,
    rescale: '0,4231308',
    supportedWeightings: ['population', 'solar_capacity'],
  },
  {
    id: 'weather_summary',
    label: 'Weather Summary',
    juaId: 'weather_summary',
    externalId: 'weather_summary',
    originalUnit: '',
    displayUnit: '',
    description: 'Overall weather situation with icons and temperatures over time',
    showTimeSeries: true,
    rescale: '',
    supportedWeightings: [],
  },
] as const;

export const RUN_TIMES = ['00', '06', '12', '18'] as const;
